/**
 * Singleton MQTT connection manager and topic router.
 *
 * This is a plain TypeScript class (not a React component) that owns
 * the MQTT client lifecycle, tracks subscriptions, and routes incoming
 * messages to registered TopicManagers based on topic namespace.
 *
 * Topic structure: {event_id}/{faction_id}/{namespace}/{event_type}
 *   - Position 2 (namespace) selects the TopicManager
 *   - Position 3 (event_type) is forwarded to the manager
 */

import mqtt, { IClientOptions, MqttClient } from "mqtt";
import { TopicManager } from "./types";

type TopicCallback = (topic: string, payload: Buffer) => void;

/**
 * Provides a fresh broker URL + MQTT JWT on demand. MqttService calls
 * this whenever the broker drops the connection (almost always because
 * the embedded JWT expired) so the reconnect attempt always presents a
 * non-stale token.
 */
type TokenProvider = () => Promise<{ url: string; token: string }>;

class MqttService {
    private client: MqttClient | null = null;
    private managers: Map<string, TopicManager> = new Map();
    private subscribedTopics: Set<string> = new Set();
    private topicCallbacks: Map<string, Set<TopicCallback>> = new Map();
    private tokenProvider: TokenProvider | null = null;
    /** Guards against parallel reconnects when several "close" events race. */
    private refreshInFlight = false;

    connect(url: string, token?: string): void {
        if (this.client) return;
        const options: IClientOptions = {
            // Disable mqtt.js's built-in auto-reconnect — it would re-use
            // the (now-expired) password it captured at connect time and
            // cycle "reconnecting → fails → reconnecting" forever. We
            // handle reconnects explicitly via [handleClose] below, which
            // fetches a fresh token first.
            reconnectPeriod: 0,
        };
        if (token !== undefined) {
            options.username = "jwt";
            options.password = token;
        }
        this.client = mqtt.connect(url, options);
        this.client.on("message", this.handleMessage.bind(this));
        this.client.on("connect", () => {
            console.log("MQTT connected");
            // Apply (or re-apply) all desired subscriptions. Covers initial
            // connect, post-reconnect-with-fresh-token, etc.
            for (const topic of this.subscribedTopics) {
                this.client?.subscribe(topic);
            }
        });
        this.client.on("error", (err) => console.error("MQTT error:", err));
        // "close" fires whenever the broker drops the connection — JWT
        // expiry, network blip, RabbitMQ restart. We don't distinguish:
        // any close triggers a fresh-token reconnect.
        this.client.on("close", () => this.handleClose());
    }

    /**
     * Register the token-provider callback that drives auto-reconnect
     * with a freshly-minted MQTT JWT. Called once by MqttProvider on
     * mount.
     */
    setTokenProvider(provider: TokenProvider | null): void {
        this.tokenProvider = provider;
    }

    /**
     * Handle an unexpected close event by refreshing the token and
     * reconnecting. Disconnects from disconnect() (subscribedTopics
     * cleared) are NOT auto-reconnected — they are intentional.
     */
    private async handleClose(): Promise<void> {
        if (this.refreshInFlight) return;
        if (this.subscribedTopics.size === 0) return; // intentional disconnect
        if (!this.tokenProvider) {
            console.warn(
                "[MQTT] connection closed but no tokenProvider registered — cannot reconnect"
            );
            return;
        }
        this.refreshInFlight = true;
        try {
            console.log("[MQTT] connection closed; fetching fresh token and reconnecting");
            const { url, token } = await this.tokenProvider();
            // Tear down the dead client and re-open with the fresh creds.
            // Don't call reconnect() (which would re-enter this code path);
            // null the client first and call connect() directly.
            if (this.client) {
                this.client.removeAllListeners();
                this.client.end(true);
                this.client = null;
            }
            this.connect(url, token);
        } catch (err) {
            console.error("[MQTT] auto-reconnect token fetch failed:", err);
        } finally {
            this.refreshInFlight = false;
        }
    }

    /**
     * Tear down the current client and reconnect with a new token. The
     * subscribedTopics set is preserved so the "connect" handler will
     * re-subscribe automatically on the new client.
     */
    reconnect(url: string, token: string): void {
        if (this.client) {
            this.client.removeAllListeners();
            this.client.end(true);
            this.client = null;
        }
        this.connect(url, token);
    }

    disconnect(): void {
        this.subscribedTopics.clear();
        this.client?.end();
        this.client = null;
    }

    subscribe(topic: string): void {
        if (this.subscribedTopics.has(topic)) return;
        this.subscribedTopics.add(topic);
        // If the client is already connected, subscribe immediately.
        // Otherwise the "connect" handler will pick it up from the set.
        if (this.client?.connected) {
            this.client.subscribe(topic);
        }
    }

    unsubscribe(topic: string): void {
        if (!this.subscribedTopics.has(topic)) return;
        this.subscribedTopics.delete(topic);
        if (this.client?.connected) {
            this.client.unsubscribe(topic);
        }
    }

    publish(topic: string, payload: Uint8Array): void {
        if (!this.client) return;
        this.client.publish(topic, payload as any);
        console.log(`[MQTT] → ${topic} (${payload.byteLength} bytes)`);
    }

    /**
     * Subscribe to a topic with a per-topic callback.
     *
     * Unlike the manager-based routing (which expects the standard
     * 4-segment topic structure), this allows any topic pattern and
     * delivers raw messages to the provided callback.
     */
    subscribeWithCallback(topic: string, callback: TopicCallback): void {
        if (!this.topicCallbacks.has(topic)) {
            this.topicCallbacks.set(topic, new Set());
        }
        this.topicCallbacks.get(topic)!.add(callback);
        this.subscribe(topic);
    }

    /**
     * Remove a previously registered per-topic callback.
     * Unsubscribes from the MQTT topic when no callbacks remain.
     */
    unsubscribeWithCallback(topic: string, callback: TopicCallback): void {
        const callbacks = this.topicCallbacks.get(topic);
        if (!callbacks) return;
        callbacks.delete(callback);
        if (callbacks.size === 0) {
            this.topicCallbacks.delete(topic);
            this.unsubscribe(topic);
        }
    }

    registerManager(namespace: string, manager: TopicManager): void {
        this.managers.set(namespace, manager);
    }

    unregisterManager(namespace: string): void {
        this.managers.delete(namespace);
    }

    /**
     * Check whether an MQTT topic matches a subscription pattern.
     * Supports `+` (single-level) and `#` (multi-level) wildcards.
     */
    private topicMatchesPattern(pattern: string, topic: string): boolean {
        const patternParts = pattern.split("/");
        const topicParts = topic.split("/");

        for (let i = 0; i < patternParts.length; i++) {
            if (patternParts[i] === "#") return true;
            if (i >= topicParts.length) return false;
            if (patternParts[i] !== "+" && patternParts[i] !== topicParts[i]) return false;
        }

        return patternParts.length === topicParts.length;
    }

    private handleMessage(topic: string, payload: Buffer): void {
        console.log(`[MQTT] ← ${topic} (${payload.byteLength} bytes)`);

        // Dispatch to per-topic callbacks (wildcard-aware matching)
        let matched = false;
        for (const [pattern, callbacks] of this.topicCallbacks) {
            if (this.topicMatchesPattern(pattern, topic)) {
                matched = true;
                for (const cb of callbacks) {
                    try {
                        cb(topic, payload);
                    } catch (e) {
                        console.error(`[MQTT] Topic callback failed for "${topic}":`, e);
                    }
                }
            }
        }

        // Manager-based routing: {event_id}/{faction_id}/{namespace}/{event_type}
        const parts = topic.split("/");
        if (parts.length < 4) return;
        const namespace = parts[2];
        const eventType = parts[3];
        const manager = this.managers.get(namespace);
        if (manager) {
            try {
                manager.handleMessage(eventType, payload);
            } catch (e) {
                console.error(
                    `[MQTT] Manager "${namespace}" failed on "${eventType}":`,
                    e
                );
            }
        } else if (!matched) {
            console.warn(`[MQTT] No manager for namespace "${namespace}"`);
        }
    }
}

export const mqttService = new MqttService();
